import { AnalyticsService } from '@core/services';
import { AngularLayout, FlexLayout } from '@rhtml/modifiers';
import { Inject } from '@rxdi/core';
import { Component, css, html, LitElement } from '@rxdi/lit-html';
import { Animation, Parallax, ScrollObserver } from '@shared/modifiers';
import { ScrollToService } from '@shared/services/scroll/scroll-to.service';
import { ReactivumTheme } from '@shared/styles/theme';

/**
 * @customElement hero-component
 */
@Component({
  selector: 'hero-component',
  styles: [
    css`
      ${ReactivumTheme}

      :host {
        display: block;
        color: var(--text-primary);
      }

      .hero-section {
        padding: 120px 0 100px 0;
        background: linear-gradient(135deg, var(--bg-secondary) 0%, var(--border-light) 50%, var(--bg-tertiary) 100%);
        text-align: center;
        position: relative;
        overflow: hidden;
      }

      .hero-section::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400"><defs><linearGradient id="bgGrad" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" style="stop-color:%230066ff;stop-opacity:0.03"/><stop offset="50%" style="stop-color:%234f46e5;stop-opacity:0.02"/><stop offset="100%" style="stop-color:%230066ff;stop-opacity:0.03"/></linearGradient></defs><rect width="400" height="400" fill="url(%23bgGrad)"/><g opacity="0.4"><!-- Floating particles --><circle cx="50" cy="50" r="1.5" fill="%230066ff" opacity="0.3"/><circle cx="150" cy="80" r="1" fill="%230066ff" opacity="0.2"/><circle cx="250" cy="120" r="2" fill="%230066ff" opacity="0.25"/><circle cx="350" cy="60" r="1.5" fill="%230066ff" opacity="0.3"/><circle cx="100" cy="180" r="1" fill="%230066ff" opacity="0.2"/><circle cx="200" cy="220" r="1.5" fill="%230066ff" opacity="0.25"/><circle cx="300" cy="160" r="1" fill="%230066ff" opacity="0.3"/><circle cx="50" cy="280" r="2" fill="%230066ff" opacity="0.2"/><circle cx="150" cy="320" r="1.5" fill="%230066ff" opacity="0.25"/><circle cx="250" cy="260" r="1" fill="%230066ff" opacity="0.3"/><circle cx="350" cy="300" r="1.5" fill="%230066ff" opacity="0.2"/><!-- Subtle grid lines --><path d="M 0 100 L 400 100" stroke="%230066ff" stroke-width="0.5" opacity="0.1"/><path d="M 0 200 L 400 200" stroke="%230066ff" stroke-width="0.5" opacity="0.1"/><path d="M 0 300 L 400 300" stroke="%230066ff" stroke-width="0.5" opacity="0.1"/><path d="M 100 0 L 100 400" stroke="%230066ff" stroke-width="0.5" opacity="0.1"/><path d="M 200 0 L 200 400" stroke="%230066ff" stroke-width="0.5" opacity="0.1"/><path d="M 300 0 L 300 400" stroke="%230066ff" stroke-width="0.5" opacity="0.1"/><!-- Diagonal accent lines --><path d="M 0 0 L 100 100" stroke="%230066ff" stroke-width="0.3" opacity="0.08"/><path d="M 100 0 L 200 100" stroke="%230066ff" stroke-width="0.3" opacity="0.08"/><path d="M 200 0 L 300 100" stroke="%230066ff" stroke-width="0.3" opacity="0.08"/><path d="M 300 0 L 400 100" stroke="%230066ff" stroke-width="0.3" opacity="0.08"/><path d="M 0 100 L 100 200" stroke="%230066ff" stroke-width="0.3" opacity="0.08"/><path d="M 100 100 L 200 200" stroke="%230066ff" stroke-width="0.3" opacity="0.08"/><path d="M 200 100 L 300 200" stroke="%230066ff" stroke-width="0.3" opacity="0.08"/><path d="M 300 100 L 400 200" stroke="%230066ff" stroke-width="0.3" opacity="0.08"/><path d="M 0 200 L 100 300" stroke="%230066ff" stroke-width="0.3" opacity="0.08"/><path d="M 100 200 L 200 300" stroke="%230066ff" stroke-width="0.3" opacity="0.08"/><path d="M 200 200 L 300 300" stroke="%230066ff" stroke-width="0.3" opacity="0.08"/><path d="M 300 200 L 400 300" stroke="%230066ff" stroke-width="0.3" opacity="0.08"/><path d="M 0 300 L 100 400" stroke="%230066ff" stroke-width="0.3" opacity="0.08"/><path d="M 100 300 L 200 400" stroke="%230066ff" stroke-width="0.3" opacity="0.08"/><path d="M 200 300 L 300 400" stroke="%230066ff" stroke-width="0.3" opacity="0.08"/><path d="M 300 300 L 400 400" stroke="%230066ff" stroke-width="0.3" opacity="0.08"/><!-- Small decorative elements --><rect x="75" y="75" width="2" height="2" fill="%230066ff" opacity="0.15"/><rect x="175" y="175" width="1.5" height="1.5" fill="%230066ff" opacity="0.12"/><rect x="275" y="275" width="2" height="2" fill="%230066ff" opacity="0.15"/><rect x="125" y="125" width="1.5" height="1.5" fill="%230066ff" opacity="0.12"/><rect x="225" y="225" width="2" height="2" fill="%230066ff" opacity="0.15"/><rect x="325" y="325" width="1.5" height="1.5" fill="%230066ff" opacity="0.12"/></g></svg>');
        opacity: 0.8;
        animation: patternFloat 30s ease-in-out infinite;
      }

      @keyframes patternFloat {
        0%,
        100% {
          transform: translateY(0px) translateX(0px);
          opacity: 0.8;
        }
        25% {
          transform: translateY(-5px) translateX(2px);
          opacity: 0.9;
        }
        50% {
          transform: translateY(-2px) translateX(-1px);
          opacity: 0.7;
        }
        75% {
          transform: translateY(-8px) translateX(1px);
          opacity: 0.85;
        }
      }

      .hero-content {
        position: relative;
        z-index: 2;
        max-width: 800px;
        margin: 0 auto;
      }

      .hero-title {
        font-size: 3.5rem;
        font-weight: 700;
        margin-bottom: 24px;
        background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        line-height: 1.2;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Inter', sans-serif;
      }

      .hero-subtitle {
        font-size: 1.25rem;
        color: var(--text-secondary);
        max-width: 600px;
        margin: 0 auto 40px auto;
        line-height: 1.6;
        font-weight: 400;
      }

      .hero-buttons {
        display: flex;
        gap: 16px;
        justify-content: center;
        flex-wrap: wrap;
        margin-top: 40px;
      }

      .primary-button {
        background: var(--primary-color);
        color: white;
        padding: 16px 32px;
        border-radius: var(--radius-lg);
        text-decoration: none;
        font-weight: 600;
        font-size: 1rem;
        transition: all 0.3s ease;
        border: none;
        cursor: pointer;
        box-shadow: 0 4px 12px rgba(0, 102, 255, 0.3);
      }

      .primary-button:hover {
        background: var(--primary-dark);
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(0, 102, 255, 0.4);
      }

      .secondary-button {
        background: white;
        color: var(--primary-color);
        padding: 16px 32px;
        border-radius: var(--radius-lg);
        text-decoration: none;
        font-weight: 600;
        font-size: 1rem;
        transition: all 0.3s ease;
        border: 2px solid var(--border-light);
        cursor: pointer;
      }

      .secondary-button:hover {
        border-color: var(--primary-color);
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
      }

      .hero-stats {
        display: flex;
        justify-content: center;
        gap: 60px;
        margin-top: 80px;
        flex-wrap: wrap;
      }

      .stat-item {
        text-align: center;
      }

      .stat-number {
        font-size: 2.5rem;
        font-weight: 700;
        color: var(--primary-color);
        display: block;
        line-height: 1;
      }

      .stat-label {
        font-size: 0.9rem;
        color: var(--text-secondary);
        margin-top: 8px;
        font-weight: 500;
      }

      @media (max-width: 768px) {
        .hero-section {
          padding: 100px 0 80px 0;
        }

        .hero-title {
          font-size: 2.5rem;
        }

        .hero-subtitle {
          font-size: 1.1rem;
        }

        .hero-buttons {
          flex-direction: column;
          align-items: center;
        }

        .hero-stats {
          gap: 40px;
        }
      }
    `,
  ],
  modifiers: [...FlexLayout, ...AngularLayout, Animation, ScrollObserver, Parallax],
  template(this: HeroComponent) {
    return html`
      <section class="hero-section" id="hero" scroll-observer threshold="0.3">
        <div class="container">
          <div class="hero-content" parallax speed="0.1" direction="up">
            <h1 class="hero-title" animated="fadeInDown" delay="0.3s">Modern Web Development Solutions</h1>
            <p class="hero-subtitle" animated="fadeInUp" delay="0.6s">
              Revolutionizing web development with innovative frameworks and tools that prioritize developer experience,
              code quality, and system maintainability.
            </p>

            <div class="hero-buttons" animated="fadeInUp" delay="0.9s">
              <a
                @click=${() => {
                  this.analytics.track('hero_cta_click', { button_text: 'Contact Us', action: 'navigate' });
                  this.scrollToSection('contact-component');
                }}
                class="primary-button"
                >Contact Us</a
              >
              <a
                @click=${() => {
                  this.analytics.track('hero_cta_click', { button_text: 'Explore Frameworks', action: 'navigate' });
                  this.scrollToSection('frameworks-component');
                }}
                class="secondary-button"
                >Explore Frameworks</a
              >
            </div>

            <div class="hero-stats" animated="fadeInUp" delay="1.2s">
              <div class="stat-item" animated="zoomIn" delay="1.4s">
                <span class="stat-number">6+</span>
                <span class="stat-label">Years of Innovation</span>
              </div>
              <div class="stat-item" animated="zoomIn" delay="1.6s">
                <span class="stat-number">10+</span>
                <span class="stat-label">Happy Custumers</span>
              </div>
              <div class="stat-item" animated="zoomIn" delay="1.8s">
                <span class="stat-number">100+</span>
                <span class="stat-label">Open Source Packages</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    `;
  },
})
export class HeroComponent extends LitElement {
  @Inject(AnalyticsService)
  private analytics: AnalyticsService;

  @Inject(ScrollToService)
  private scrollToService: ScrollToService;

  scrollToSection(componentName: string) {
    this.scrollToService.scrollToElement(componentName);
  }
}
